import { Button, Dropdown, Spin, Tag, Modal, Tooltip, Card } from "antd";
import { useState } from "react";
import InfiniteTable from "../utils/InfiniteTable";
import {
	DeleteFilled,
	DownOutlined,
	EditFilled,
	LockFilled,
	UnlockFilled,
} from "@ant-design/icons";
import Toast from "../../helpers/Toast";
import { catchError } from "../../services/DaoService";
import { ModeleService } from "../../services/ModeleService";
import { NavLink } from "react-router-dom";
import ModeleEditForm from "./ModeleEditForm";
import PropTypes from "prop-types";

const ModeleList = ({
	paginationData,
	setPaginationData,
	modeles,
	setModeles,
	searchParams,
}) => {
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [updateModalVisible, setUpdateModalVisible] = useState(false);
	const [selectedModele, setSelectedModele] = useState(null);

	const loadMore = () => {
		if (paginationData?.next_page_url && !loadingMore) {
			setLoadingMore(true);
			ModeleService.post(paginationData.next_page_url, searchParams)
				.then((response) => {
					if (!response) {
						return;
					}
					setPaginationData(response.data);
					setModeles((modeles) => [...modeles, ...response.data.data]);
				})
				.catch((error) => {
					catchError(error);
				})
				.finally(() => {
					setLoadingMore(false);
				});
		}
	};
	const handleDelete = (idOrUid) => {
		setLoading(true);
		ModeleService.getInstance()
			.remove(idOrUid)
			.then((response) => {
				Toast.success(response.message);
				setModeles((items) => items.filter((item) => item.uid !== idOrUid));
			})
			.catch((error) => {
				catchError(error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const openEditModal = (item) => {
		setSelectedModele(item);
		setUpdateModalVisible(true);
	};

	const handleEnable = (modele) => {
		ModeleService.getInstance()
			.enable(modele.uid)
			.then((response) => {
				Toast.success(response.message);
				setModeles((modeles) =>
					modeles.map((u) => (u.uid === response.data.uid ? response.data : u))
				);
			})
			.catch((error) => {
				Toast.error(error.message);
			});
	};

	const handleDisable = (modele) => {
		ModeleService.getInstance()
			.disable(modele.uid)
			.then((response) => {
				Toast.success(response.message);
				setModeles((modeles) =>
					modeles.map((u) => (u.uid === response.data.uid ? response.data : u))
				);
			})
			.catch((error) => {
				Toast.error(error.message);
			});
	};

	const handleUpdate = (item) => {
		setModeles((modeles) =>
			modeles.map((u) => (u.uid === item.uid ? item : u))
		);
		setSelectedModele(null);
		setUpdateModalVisible(false);
	};

	const columns = [
		{
			title: "Nom",
			dataIndex: "nom",
			key: "nom",
			width: 400,
			render: (nom, item) => (
				<NavLink to={`/modele/${item.uid}`}>{nom}</NavLink>
			),
			ellipsis: true,
		},
		{
			title: "Code",
			dataIndex: "code",
			key: "code",
			width: 80,
		},
		{
			title: "Couleur",
			dataIndex: "couleur",
			key: "couleur",
			width: 150,
			render: (couleur) =>
				couleur ? (
					<Card classNames={{ body: 'p-2' }}
						style={{
							background: couleur,
							width: "50%",
						}}
					></Card>
				) : (
					"N/A"
				),
		},
		{
			title: "Nombre d'étapes",
			dataIndex: "etape_modeles_count",
			key: "etape_modeles_count",
			width: 200,
			render: (etape_modeles_count) => <Tag color={etape_modeles_count > 2 ? "green" : "red"}>{etape_modeles_count} étapes associées</Tag>,
			sorter: (a, b) => a.etape_modeles_count - b.etape_modeles_count,
		},
		{
			title: "Nombre d'étapes Résolues",
			dataIndex: "etape_modele_resolues_count",
			key: "etape_modele_resolues_count",
			width: 250,
			render: (etape_modeles_count) => <Tag color={etape_modeles_count > 0 ? "green" : "red"}>{etape_modeles_count} étapes associées</Tag>,
			sorter: (a, b) => a.etape_modeles_count - b.etape_modeles_count,
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			render: (description) => (
				<Tooltip title={description}>
					{description && description.length > 100
						? description.substring(0, 100) + "..."
						: (description ? description : "N/A")}
				</Tooltip>
			),
			width: 400,
		},
		{
			title: "Statut",
			dataIndex: "enabled",
			key: "enabled",
			render: (enabled) =>
				enabled ? (
					<Tag color='success'>Actif</Tag>
				) : (
					<Tag color='error'>Inactif</Tag>
				),
			width: 150,
		},
		{
			title: "Défault",
			dataIndex: "default",
			key: "default",
			render: (defaultModele) =>
				defaultModele ? (
					<Tag color='success'>Oui</Tag>
				) : (
					<Tag color='error'>Non</Tag>
				),
			width: 150,
		},
		{
			title: "Actions",
			key: "actions",
			render: (_, record) => (
				<Dropdown
					menu={{
						items: [
							{
								key: "edit",
								label: "Modifier",
								icon: <EditFilled />,
								onClick: () => openEditModal(record),
							},
							{
								key: "toggle",
								label: record.enabled ? "Désactiver" : "Activer",
								icon: record.enabled ? <LockFilled /> : <UnlockFilled />,
								onClick: () => {
									Modal.confirm({
										title: `Voulez-vous ${record.enabled ? "désactiver" : "activer"
											} cet modele?`,
										okText: record.enabled ? "Désactiver" : "Activer",
										cancelText: "Annuler",
										okType: record.enabled ? "danger" : "primary",
										onOk: () =>
											record.enabled
												? handleDisable(record)
												: handleEnable(record),
									});
								},
							},
							{
								key: "delete",
								label: "Supprimer",
								icon: <DeleteFilled />,
								danger: true,
								onClick: () => {
									Modal.confirm({
										title: `Voulez-vous vraiment procéder à la suppression ?`,
										okText: "Confirmer",
										cancelText: "Annuler",
										okType: "danger",
										onOk: () => handleDelete(record.uid),
									});
								},
							},
						],
					}}
				>
					<Button>
						Actions <DownOutlined />
					</Button>
				</Dropdown>
			),
			width: 150,
			fixed: "right",
		},
	];

	return (
		<>
			<Spin spinning={loading}>
				<InfiniteTable
					rowKey={"uid"}
					dataSource={modeles}
					paginationData={paginationData}
					loadMore={loadMore}
					loading={loadingMore}
					columns={columns}
				/>
			</Spin>
			{selectedModele && (
				<ModeleEditForm
					visible={updateModalVisible}
					uid={selectedModele.uid}
					onUpdate={handleUpdate}
					onCancel={() => setUpdateModalVisible(false)}
				/>
			)}
		</>
	);
};
export default ModeleList;

ModeleList.propTypes = {
	paginationData: PropTypes.object.isRequired,
	searchParams: PropTypes.object,
	setPaginationData: PropTypes.func.isRequired,
	modeles: PropTypes.array.isRequired,
	setModeles: PropTypes.func.isRequired,
};
